import { useData, useEventListener } from "#root/utility/hooks";
import type RemoteCollection from "#root/utility/remote-collection";
import { Invalid, Neutral, Valid, type Validator } from "#root/utility/validators";
import { type InputHTMLAttributes, useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useFieldSet } from "./fieldset";

export type AutocompleteProps<ListRowType extends z.ZodType<{ id: string | number }>> = InputHTMLAttributes<HTMLInputElement> & {
  collection: RemoteCollection<ListRowType, any>;
  validator?: Validator<z.infer<ListRowType>["id"]>;
  required?: boolean;
  dirty?: boolean;
  defaultValue?: z.infer<ListRowType>["id"];
};

export const AutoCompleteSchema = z.object({
  rows: z.array(
    z.object({
      name: z.string(),
      value: z.union([z.string(), z.number()]),
      extra: z.string().nullish()
    })
  )
});

export function AutoCompleteField<
  ListRowType extends z.ZodType<{ id: string }> | z.ZodType<{ id: number}>,
>({ collection, validator, required, dirty: startDirty = false, defaultValue = "", className = "", ...inputProps }: AutocompleteProps<ListRowType>) {
  const fieldSet = useFieldSet();

  const [dirty, setDirty] = useState(startDirty);
  const [value, setValue] = useState<z.infer<ListRowType>["id"] | undefined>(fieldSet?.field?.value == null ? defaultValue : fieldSet?.field?.value as z.infer<ListRowType>["id"]);
  const [search, setSearch] = useState<string | null>(null);

  const [text, setText] = useData(async () => {
    if (!value) {
      return null;
    }

    const response = await collection.action("autocomplete", { value });

    const parsed = AutoCompleteSchema.parse(response);

    return parsed.rows?.[0].name;
  }, [collection, value]);

  const [results] = useData(async () => {
    if (!search) {
      return null;
    }

    const response = await collection.action("autocomplete", { q: search });

    return AutoCompleteSchema.parse(response);
  }, [collection, search]);

  const { validity, hint } = useMemo(() => {
    if (value == undefined || value === "") {
      if (required) {
        return { validity: Invalid, hint: "Required" };
      }

      return { validity: Neutral, hint: "Optional" };
    }

    if (validator) {
      return validator(value) || { validity: Valid, hint: "Valid" };
    }

    return { validity: Valid, hint: "Valid" };
  }, [value, required, validator]);

  useEffect(() => {
    fieldSet?.mutate(object => ({
      ...object,
      dirty,
      validity,
      hint,
      value,
    }));
  }, [fieldSet, dirty, validity, hint, value]);

  switch (validity) {
    case Valid: {
      className += "valid";

      break;
    }

    case Invalid: {
      className += "invalid";

      break;
    }

    case Neutral: {
      className += "neutral";

      break;
    }
    // No default
  }

  if (results) {
    className += " open";
  }

  const handleChange = useCallback(evnt => {
    setSearch(evnt.target.value);
  }, []);

  const handleClick = useCallback(evnt => {
    setSearch("");
    evnt.preventDefault();
    evnt.stopPropagation();
  }, []);

  const handleChildClick = useCallback(
    evnt => {
      const { value, name } = evnt.currentTarget.dataset;

      setValue(value);
      setText(name);
      setSearch(null);
      setDirty(true);
      evnt.preventDefault();
      evnt.stopPropagation();
    },
    [setText],
  );

  const handleBodyClick = useCallback(() => {
    setSearch(null);
  }, []);

  useEventListener(document.body, "click", handleBodyClick);

  return (
    <div className={"auto-complete " + className}>
      <input
        {...inputProps}
        className={className}
        autoComplete="false"
        value={search ?? text ?? undefined}
        onChange={handleChange}
        onInput={handleChange}
        onClick={handleClick}
      />
      <div className="outer">
        <div className={results ? "form-dropdown active" : "form-dropdown"}>
          {results ? (
            <div className="items">
              {results?.rows?.map(row => (
                <a key={row.value} href="#" data-value={row.value} data-name={row.name} onMouseDown={handleChildClick}>
                  {row.name}
                  {row.extra ? <div className="extra">{row.extra}</div> : null}
                </a>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
